"use client";

import { FeatherLightbulb } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import type { RegisteredUnitFormValues } from "@/components/registered-units/RegisteredUnitFormDialog";
import { RegisteredUnitFormDialog } from "@/components/registered-units/RegisteredUnitFormDialog";
import type {
  RegisteredUnit,
  RegisteredUnitCopy,
} from "@/components/registered-units/RegisteredUnitTable";
import { RegisteredUnitTable } from "@/components/registered-units/RegisteredUnitTable";
import {
  useCreateEmergencyLightCommandControllerCreate,
  useListEmergencyLightsQueryControllerList,
  useUpdateEmergencyLightCommandControllerUpdate,
} from "@/generated/emergency-lights/emergency-lights";

const COPY: RegisteredUnitCopy = {
  title: "Emergency Lights",
  description: "Register each emergency light so none is missed during a check",
  addLabel: "Add Light",
  emptyTitle: "No emergency lights yet",
  emptyBody:
    "Register each emergency light and its location so staff can record the monthly flick test and the visual check against every one.",
  emptyIcon: <FeatherLightbulb />,
  itemNoun: "lights",
  errorMessage: "Error loading emergency lights. Please try again.",
};

const FORM_COPY = {
  // Many lights sit in corridors or on landings that aren't set up as rooms,
  // so a light may be registered without one (NAU-440).
  roomRequired: false,
  unitLabel: "Light",
  namePlaceholder: "e.g., Light 1 — Main Corridor",
  notesPlaceholder: "e.g., 3-hour maintained unit, battery replaced 2026-01-10",
  noRoomsHelpText: "",
};

export function EmergencyLightTable({ nurseryId }: { nurseryId: string }) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<RegisteredUnit | null>(null);

  const {
    data: unitsData,
    isLoading,
    error,
  } = useListEmergencyLightsQueryControllerList(nurseryId, {
    includeInactive: true,
  });

  const units = useMemo(() => unitsData?.data ?? [], [unitsData]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        typeof query.queryKey[0] === "string" &&
        query.queryKey[0].includes("/emergency-lights"),
      refetchType: "all",
    });
  };

  const { mutate: createUnit, isPending: isCreating } =
    useCreateEmergencyLightCommandControllerCreate({
      mutation: {
        onSuccess: async () => {
          toast.success("Light added successfully");
          await invalidate();
          setDialogOpen(false);
          setEditingUnit(null);
        },
        onError: (e: unknown) =>
          toast.error(
            e instanceof Error ? e.message : "Failed to add emergency light",
          ),
      },
    });

  const { mutate: updateUnit, isPending: isUpdating } =
    useUpdateEmergencyLightCommandControllerUpdate({
      mutation: {
        onSuccess: async () => {
          await invalidate();
        },
        onError: (e: unknown) =>
          toast.error(
            e instanceof Error ? e.message : "Failed to update emergency light",
          ),
      },
    });

  const handleToggleActive = (unit: RegisteredUnit) =>
    updateUnit(
      {
        nurseryId,
        emergencyLightId: unit.id,
        data: { isActive: !unit.isActive },
      },
      { onSuccess: () => toast.success("Light status updated") },
    );

  const handleSubmit = (values: RegisteredUnitFormValues) => {
    if (editingUnit) {
      updateUnit(
        {
          nurseryId,
          emergencyLightId: editingUnit.id,
          // No room picked means "not in a room", so an edit clears it.
          data: {
            ...values,
            roomId: values.roomId ?? null,
            notes: values.notes ?? null,
          },
        },
        {
          onSuccess: () => {
            toast.success("Light updated successfully");
            setDialogOpen(false);
            setEditingUnit(null);
          },
        },
      );
      return;
    }
    createUnit({ nurseryId, data: values });
  };

  return (
    <>
      <RegisteredUnitTable
        units={units}
        isLoading={isLoading}
        error={error}
        copy={COPY}
        onAdd={() => {
          setEditingUnit(null);
          setDialogOpen(true);
        }}
        onEdit={(unit) => {
          setEditingUnit(unit);
          setDialogOpen(true);
        }}
        onToggleActive={handleToggleActive}
      />
      <RegisteredUnitFormDialog
        nurseryId={nurseryId}
        unit={editingUnit}
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingUnit(null);
        }}
        onSubmit={handleSubmit}
        isPending={isCreating || isUpdating}
        copy={FORM_COPY}
      />
    </>
  );
}
